'use client';

import { useCallback, useEffect, useState } from 'react';

import type { Listing, ListingVariant } from '@ps/models/lib/listing';

import { maxAvailableQuantity } from '../functions/listingDisplay';

export interface UseQuantitySelectionResult {
  quantity: number;
  setQuantity: (q: number) => void;
  /** Resolved upper bound (variant-aware + capped at
   *  `listing.maximumPurchaseQuantity`). */
  maxQuantity: number;
  /** True for derived/digital/meeting/avatar-me/token-pack/ai-pdf where the
   *  selector should not render. */
  isFixedToOne: boolean;
  /** True when the listing is sold out (zero stock for the current selection). */
  isSoldOut: boolean;
}

/**
 * Mirrors RN `useQuantityFeature`. Owns the quantity state, clamps it to the
 * variant-aware max, and resets into range when the variant changes.
 */
export function useQuantitySelection(
  listing: Pick<Listing, 'type' | 'quantity' | 'maximumPurchaseQuantity'>,
  variant?: Pick<ListingVariant, 'quantity'> | null,
): UseQuantitySelectionResult {
  const maxQty = maxAvailableQuantity(listing, variant);
  // 1 is a sane default — RN does the same. Clamp on every render so the
  // value stays valid as variant/inventory changes.
  const [quantity, setQuantityRaw] = useState(1);

  // When the variant changes, snap quantity back into range.
  useEffect(() => {
    if (maxQty > 0 && quantity > maxQty) setQuantityRaw(maxQty);
    if (quantity < 1) setQuantityRaw(1);
  }, [maxQty, quantity]);

  const setQuantity = useCallback(
    (q: number) => {
      if (Number.isNaN(q)) return;
      const clamped = Math.max(1, Math.min(maxQty, Math.floor(q)));
      setQuantityRaw(clamped);
    },
    [maxQty],
  );

  return {
    quantity,
    setQuantity,
    maxQuantity: maxQty,
    isFixedToOne: maxQty === 1 || (listing.maximumPurchaseQuantity ?? 0) === 1,
    isSoldOut: maxQty === 0,
  };
}
